/**
 * Persistent bottom-right upload dock. Shows whenever the upload manager
 * has any entries; hides itself when the list is empty.
 *
 * Driven entirely by useUploadManager(); does not own any state itself.
 */
import { useLayoutEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, X, Pause, Play, AlertTriangle, CheckCircle2 } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { useUploadManager, type UploadEntry, type UploadStatus } from './UploadManagerProvider'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function statusLabel(status: UploadStatus): string {
  switch (status) {
    case 'queued': return 'Queued'
    case 'uploading': return 'Uploading'
    case 'paused': return 'Paused'
    case 'scanning': return 'Scanning'
    case 'writing': return 'Saving'
    case 'completed': return 'Done'
    case 'failed': return 'Failed'
    case 'cancelled': return 'Cancelled'
  }
}

function progressPct(entry: UploadEntry): number {
  if (entry.status === 'completed') return 100
  if (entry.fileSize === 0) return 0
  if (entry.status === 'scanning' || entry.status === 'writing') return 100
  return Math.min(100, Math.round((entry.bytesUploaded / entry.fileSize) * 100))
}

function isTerminal(s: UploadStatus): boolean {
  return s === 'completed' || s === 'failed' || s === 'cancelled'
}

export function UploadDock() {
  const manager = useUploadManager()
  const [expanded, setExpanded] = useState(true)
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const rootRef = useRef<HTMLDivElement | null>(null)

  const visible = manager.uploads.length > 0

  // Publish our height as a CSS variable so other bottom-anchored layers
  // (toasts, etc.) can stack above us instead of overlapping. Re-runs
  // whenever the dock appears/disappears so the ref is observed only
  // while the element actually exists.
  useLayoutEffect(() => {
    if (!visible) {
      document.documentElement.style.removeProperty('--upload-dock-offset')
      return
    }
    const el = rootRef.current
    if (!el) return
    const root = document.documentElement
    const update = () => {
      root.style.setProperty('--upload-dock-offset', `${el.offsetHeight + 16}px`)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => {
      ro.disconnect()
      root.style.removeProperty('--upload-dock-offset')
    }
  }, [visible])

  if (!visible) return null

  // Partition ONCE. The dock PRIORITIZES actively-uploading rows and caps how many
  // it renders: a large folder drop makes 8,000+ queued entries, and one DOM row
  // each was ~140k nodes → jank. Show active first, then failed, then a bounded
  // slice of queued; the rest collapse into a summary line.
  const active: UploadEntry[] = []
  const failedRows: UploadEntry[] = []
  const queuedRows: UploadEntry[] = []
  let doneCount = 0
  for (const u of manager.uploads) {
    if (u.status === 'uploading' || u.status === 'scanning' || u.status === 'writing' || u.status === 'paused') active.push(u)
    else if (u.status === 'failed') failedRows.push(u)
    else if (u.status === 'queued') queuedRows.push(u)
    else doneCount++ // completed / cancelled
  }
  const inProgress = active.length + queuedRows.length
  const headerLabel = active.length > 0
    ? `Uploading ${active.length}${queuedRows.length > 0 ? ` · ${queuedRows.length.toLocaleString()} queued` : ''}`
    : queuedRows.length > 0
      ? `${queuedRows.length.toLocaleString()} queued`
      : `${manager.uploads.length} upload${manager.uploads.length !== 1 ? 's' : ''}`

  return (
    <div
      ref={rootRef}
      className={`fixed bottom-4 right-4 z-40 w-[360px] max-w-[calc(100vw-2rem)] rounded-lg shadow-xl ${themeClass} themed-bg-secondary themed-border border`}
    >
      {/* Header — clickable to expand/collapse, with Clear as a sibling
          button (nested <button>s are invalid HTML and trigger hydration errors). */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v) }}
        className="flex w-full items-center justify-between px-4 py-2.5 themed-bg-tertiary rounded-t-lg cursor-pointer select-none"
      >
        <span className="themed-text font-medium text-sm">{headerLabel}</span>
        <span className="flex items-center gap-2">
          {failedRows.length > 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); manager.clearFailed() }}
              className="text-xs themed-text-muted hover:themed-text px-2 py-0.5 rounded hover:themed-bg-secondary"
            >
              Clear failed{failedRows.length > 1 ? ` (${failedRows.length.toLocaleString()})` : ''}
            </button>
          )}
          {inProgress === 0 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); manager.clearCompleted() }}
              className="text-xs themed-text-muted hover:themed-text px-2 py-0.5 rounded hover:themed-bg-secondary"
            >
              Clear
            </button>
          )}
          {expanded ? <ChevronDown size={16} className="themed-icon" /> : <ChevronUp size={16} className="themed-icon" />}
        </span>
      </div>

      {/* List — active always shown, failed + queued each bounded, rest summarized.
          Cap failed too (#298 M4): a systematic large-drop failure (quota, auth
          expiry, network loss) migrates thousands of rows queued→failed, and
          failed entries aren't auto-cleared — spreading them uncapped reintroduces
          the exact DOM blow-up the virtualization was added to prevent. */}
      {expanded && (() => {
        const MAX_QUEUED_SHOWN = 20
        const MAX_FAILED_SHOWN = 20
        const shownFailed = failedRows.slice(0, MAX_FAILED_SHOWN)
        const hiddenFailed = failedRows.length - shownFailed.length
        const shownQueued = queuedRows.slice(0, MAX_QUEUED_SHOWN)
        const hiddenQueued = queuedRows.length - shownQueued.length
        const rows = [...active, ...shownFailed, ...shownQueued]
        const summaryParts: string[] = []
        if (hiddenFailed > 0) summaryParts.push(`+ ${hiddenFailed.toLocaleString()} failed`)
        if (hiddenQueued > 0) summaryParts.push(`+ ${hiddenQueued.toLocaleString()} queued`)
        if (doneCount > 0) summaryParts.push(`${doneCount.toLocaleString()} done`)
        return (
          <div className="max-h-[50vh] overflow-y-auto">
            {rows.map((entry) => (
              <UploadRow key={entry.id} entry={entry} manager={manager} />
            ))}
            {summaryParts.length > 0 && (
              <div className="px-4 py-2.5 text-xs themed-text-muted themed-border-secondary border-t flex items-center gap-1.5">
                <span>{summaryParts.join(' · ')}</span>
              </div>
            )}
          </div>
        )
      })()}
    </div>
  )
}

function UploadRow({ entry, manager }: { entry: UploadEntry; manager: ReturnType<typeof useUploadManager> }) {
  const pct = progressPct(entry)
  const terminal = isTerminal(entry.status)
  const failed = entry.status === 'failed'
  const completed = entry.status === 'completed'
  const cancelled = entry.status === 'cancelled'

  return (
    <div className="px-4 py-3 themed-border-secondary border-t first:border-t-0">
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            {failed && <AlertTriangle size={14} className="shrink-0 text-amber-500" />}
            {completed && <CheckCircle2 size={14} className="shrink-0 text-emerald-500" />}
            <span className="themed-text text-sm truncate" title={entry.fileName}>{entry.fileName}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs themed-text-muted">
            {/* #1090 — a large app database is replaced in the background: its outcome comes as a notice. */}
            <span>{completed && entry.replacingRecordId ? 'Uploaded — replacing the database in the background' : statusLabel(entry.status)}</span>
            <span>·</span>
            <span>{formatBytes(entry.bytesUploaded)} / {formatBytes(entry.fileSize)}</span>
          </div>
        </div>

        {/* Per-row actions */}
        <div className="flex items-center gap-1 shrink-0">
          {entry.status === 'uploading' && (
            <button
              type="button"
              onClick={() => manager.pause(entry.id)}
              className="p-1 rounded hover:themed-bg-tertiary themed-icon"
              title="Pause"
            >
              <Pause size={14} />
            </button>
          )}
          {entry.status === 'paused' && (
            <button
              type="button"
              onClick={() => manager.resume(entry.id)}
              className="p-1 rounded hover:themed-bg-tertiary themed-icon"
              title="Resume"
            >
              <Play size={14} />
            </button>
          )}
          {(failed || cancelled) && (
            <button
              type="button"
              onClick={() => manager.clear(entry.id)}
              className="p-1 rounded hover:themed-bg-tertiary themed-icon"
              title="Dismiss"
            >
              <X size={14} />
            </button>
          )}
          {!terminal && (
            <button
              type="button"
              onClick={() => manager.cancel(entry.id)}
              className="p-1 rounded hover:themed-bg-tertiary themed-icon"
              title="Cancel"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar. During scanning/writing the bar fills to 100%
          but uses an animated stripe to communicate that server-side
          work is still in flight (we don't know how long scan will take). */}
      {!cancelled && (
        <div className="mt-2 h-1 themed-bg-tertiary rounded overflow-hidden">
          <div
            className={`h-full transition-all duration-200 ${
              failed ? 'bg-amber-500'
              : completed ? 'bg-emerald-500'
              : 'bg-blue-500'
            } ${(entry.status === 'scanning' || entry.status === 'writing') ? 'upload-progress-indeterminate' : ''}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {failed && entry.error && (
        <div className="mt-1.5 text-xs text-amber-600 dark:text-amber-400 truncate" title={entry.error}>
          {entry.malware ? `Malware: ${entry.malware.threat}` : entry.error}
        </div>
      )}
    </div>
  )
}
